class QuickView extends HTMLElement {
  constructor() {
    super();
    this._onDocClick = this._onDocClick.bind(this);
    this._onKeyUp = this._onKeyUp.bind(this);
    this._onCloseClick = this._onCloseClick.bind(this);
  }

  connectedCallback() {
    // Selectors / config
    this.btnSelector = this.getAttribute('data-buttons') || '.quickview_btn';
    this.moneyFormat = this.getAttribute('data-money-format') || '{{ amount }}';

    // The modal is INSIDE this element now
    this.modal = this.querySelector('#quickView') || this.firstElementChild;
    if (!this.modal) return;

    this.loader = document.querySelector('.global-loader.loader');
    this.closeBtn = this.modal.querySelector('.modal-close');

    // Cache modal nodes
    this.form = this.modal.querySelector('form');
    this.titleLink = this.modal.querySelector('.product_title_link');
    this.titleEl = this.modal.querySelector('.product_title');
    this.imgEl = this.modal.querySelector('.quickview_product_image');
    this.priceEl = this.modal.querySelector('.product_price');
    this.stockEl = this.modal.querySelector('.product_stock');
    this.qtyEl = this.modal.querySelector('#quickview_quantity');
    this.storeInfo = this.modal.querySelector(".view_store_info")
    this.inStockEl = this.modal.querySelector('.in-stock');
    this.noStockEl = this.modal.querySelector('.out-stock');
    this.variantSelect = this.modal.querySelector(".product_options select[name='id']");
    this.optionsList = this.modal.querySelector('.product_options_list');
    this.buyBtn = this.modal.querySelector('.product-buy');
    this.addBtn = this.modal.querySelector('.product-add');
    this.buyBtnText = this.buyBtn?.querySelector('.text, .btn-text');
    this.addBtnText = this.addBtn?.querySelector('.text, .btn-text');

    // Events (document-level so buttons can be anywhere)
    document.addEventListener('click', this._onDocClick);
    window.addEventListener('keyup', this._onKeyUp);
    this.closeBtn?.addEventListener('click', this._onCloseClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);
    window.removeEventListener('keyup', this._onKeyUp);
    this.closeBtn?.removeEventListener('click', this._onCloseClick);
  }

 // ----------------- Availability helpers (integrated) -----------------
  updateVariantStatuses(variants, inputField) {
    // Container holding all option fieldsets (QuickView uses #quickview_options or .product_options_list)
    const container = inputField || this.modal.querySelector('.product_options_list') || this.optionsList || this.modal;
    const firstChecked = container.querySelector('fieldset input[type="radio"]:checked');

    // If nothing is checked yet, nothing to compute
    if (!firstChecked) return;

    // Determine variants matching the selected option1
    const selectedOptionOne = firstChecked.value;
    const selectedOptionOneVariants = variants.filter(v => v.option1 === selectedOptionOne);

    // All option groups
    const inputWrappers = [...container.querySelectorAll('fieldset')];

    inputWrappers.forEach((fieldset, index) => {
      // Skip the first group (it drives availability for the rest)
      if (index === 0) return;

      const optionInputs = [...fieldset.querySelectorAll('input[type="radio"], option')];
      const prevGroup = inputWrappers[index - 1];
      const previousOptionSelected = prevGroup.querySelector('input[type="radio"]:checked')?.value;

      // Shopify variant option fields are 1-indexed: option1, option2, option3...
      // We’ve already filtered by option1; now limit by option{index} == previous selected
      // and map to option{index+1} as available values in the current fieldset
      const availableOptionInputsValue = selectedOptionOneVariants
        .filter(variant => variant.available && variant[`option${index}`] === previousOptionSelected)
        .map(variantOption => variantOption[`option${index + 1}`]);

      this.setInputAvailability(optionInputs, availableOptionInputsValue);
    });
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      const val = input.getAttribute('value');
      const isAvailable = listOfAvailableOptions.includes(val);

      // Fallback label resolution that works with your markup (.radio_input/.color_input)
      const wrapper = input.closest('.radio_input, .color_input, .option_main') || input.closest('li');   

      if (isAvailable) {
        // Enable visual state
        if (wrapper) wrapper.classList.remove('disabled');
        // Keep visible value text consistent (mainly for <option> fallback)
        if (input.tagName === 'OPTION') input.innerText = val;
        // Ensure input isn't disabled
        wrapper?.classList.remove('disabled');
      } else {
        if (wrapper) wrapper.classList.add('disabled');
      }
    });
  }

  // ----------------- Events -----------------
  async _onDocClick(e) {
    const btn = e.target.closest(this.btnSelector);
    if (!btn) return;

    const handle = btn.getAttribute('data-handle');
    if (!handle) return;
    btn.setAttribute('aria-expanded', 'true');
    this.loader?.classList.remove('hidden');

    await this.renderQuickView(handle, btn);
    document.documentElement.classList.add('overflow-hidden');
    this.loader?.classList.add('hidden');
    this.modal.classList.add('quickview_active');
  }

  _onCloseClick() {
    this._close();
  }

  _onKeyUp(e) {
    if (e.key === 'Escape' && this.modal.classList.contains('quickview_active')) {
      this._close();
    }
  }

  _close() {
    this.modal.classList.remove('quickview_active');
    document.documentElement.classList.remove('overflow-hidden');
    // Reset any aria-expanded on buttons
    document.querySelectorAll(this.btnSelector).forEach(b => b.setAttribute('aria-expanded', 'false'));
  }

  // ----------------- Utils -----------------
  _money(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = format || this.moneyFormat;
    const token = (fmt.match(/\{\{\s*(\w+)\s*\}\}/) || [,'amount'])[1];
    const withDelims = (num, precision = 2, thousands = ',', decimal = '.') => {
      if (isNaN(num) || num == null) return 0;
      const n = (num / 100).toFixed(precision).split('.');
      const dollars = n[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return dollars + (n[1] ? decimal + n[1] : '');
    };
    let value = '';
    switch (token) {
      case 'amount': value = withDelims(cents, 2); break;
      case 'amount_no_decimals': value = withDelims(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelims(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelims(cents, 0, '.', ','); break;
      default: value = withDelims(cents, 2);
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, value);
  }

  _setPrice(container, priceCents, compareCents) {
    if (!container) return;
    let current = container.querySelector('.current-price');
    if (!current) {
      current = document.createElement('span');
      current.className = 'current-price';
      container.append(current);
    }
    current.innerText = newFormatMoney(priceCents);
    current.innerText = newFormatMoney(priceCents);
    let compareEl = container.querySelector('.compare-at');
    if (!compareCents || compareCents <= 0 || compareCents === priceCents) {
      compareEl?.remove();
    } else {
      if (!compareEl) {
        compareEl = document.createElement('span');
        compareEl.className = 'compare-at line-through opacity-70';
        container.append(compareEl);
      }
      compareEl.innerText = newFormatMoney(compareCents);
    }    
  }

  _setStock(available, isPreorder) {
    if (!this.addBtn || !this.buyBtn || !this.stockEl) return;
    // Normalize button types to avoid native submit
    this.modal.querySelectorAll('.product-add, .product-buy').forEach(b => {
      b.setAttribute('type', 'button');
    });
    
    if (available) {
      this.stockEl.innerText = 'In Stock';
      if (isPreorder) {
        this.addBtnText && (this.addBtnText.textContent = 'Preorder');
        this.buyBtn.style.display = 'none';
      } else {
        this.addBtnText && (this.addBtnText.textContent = 'Add to Cart');
        this.buyBtn.style.display = 'flex';
      }
      this.buyBtnText && (this.buyBtnText.textContent = 'Buy Now');
      this.buyBtn.disabled = false;
      this.addBtn.disabled = false;
      this.qtyEl.parentElement.querySelectorAll("button,input").forEach(el=>el.removeAttribute("disabled"))
      this.inStockEl && (this.inStockEl.style.display = 'block');
      this.noStockEl && (this.noStockEl.style.display = 'none');
    } else {
      this.stockEl.innerText = 'Out of Stock';
      this.addBtnText && (this.addBtnText.textContent = 'Out of Stock');
      this.buyBtnText && (this.buyBtnText.textContent = 'Out of Stock');
      this.buyBtn.disabled = true;
      this.addBtn.disabled = true;
      this.qtyEl.parentElement.querySelectorAll("button,input").forEach(el=>el.setAttribute("disabled",true))
      this.noStockEl && (this.noStockEl.style.display = 'block');
      this.inStockEl && (this.inStockEl.style.display = 'none');
    }
  }

  _applyColorSwatchesFromCard(triggerBtn) {
    const card = triggerBtn.closest('.product_item_main');
    if (!card) return;

    const swatches = card.querySelectorAll('.color_swatch .label-inner');
    let optionName = swatches[0]?.closest(".option_field")?.querySelector("legend")?.getAttribute("data-name") || "Color"
    const colors = Array.from(swatches).map(s => ({
      name: s.getAttribute('data-val'),
      colorVal: s.getAttribute('data-color') || '#c4c4c4'
    }));
    if (!colors.length) return;

    this.modal.querySelectorAll('fieldset').forEach((field) => {
      const legend = field.querySelector('legend');
      if (!legend || !legend.textContent.includes(optionName)) return;
      const inputs = field.querySelectorAll('input');
      inputs.forEach((input, i) => {
        const label = input.closest('li')?.querySelector('label');
        if (!label) return;
        label.classList.add('color-active');   
        label.parentElement.classList.add("color_input")     
        label.style.backgroundColor = colors[i]?.colorVal || '#c4c4c4';
        if (!colors[i]?.colorVal) label.classList.add('default_color');       
      });
    });
  }

  sliderHTML(images,title) {
    if (!images || !images.length) return '';

    let slideShow = `
    <slide-show
  data-loop="false"
  data-autoplay="false"
  data-autoplay-delay="2000"
  data-speed="2500"
  data-slides-per-view="1"
  data-pagination-type="bullets"
  data-space-between="20"
  data-zoom-enabled="false"
  data-customize-breakpoints="false"
  data-slides-per-view-mobile="1"
  data-slides-per-view-tablet="1"
  data-slides-per-view-desktop="1"
  data-allow-touch="true"
  data-enable-thumbs="false"
  data-thumb-cols="3"
  data-thumb-slider-alignment="horizontal"
  data-disable-on-mobile="false"
  data-disable-on-desktop="false"
>
    <div class="swiper swiper__main" id="quickview_swiper">
     <div class="swiper-wrapper">
      ${images.map(img => `
      <div class="swiper-slide">
            <img src="${img}" alt="${title}" width="800" height="800" loading="lazy" />
        </div>`).join("")}
     </div>

     <div class="base-slider-controls hero-slider">
      <div class="container flex items-center justify-between gap-5">
        <div class="base-slider-controls-left shrink-0">       
            <div class="swiper-pagination-el"></div>
        </div>        
          <div class="base-slider-controls-right">
            <!-- Add Arrows -->
            <div class="swiper-button-prev">             
            </div>
            <div class="swiper-button-next">              
            </div>
          </div>
      </div>
     </div>
  </div>
  </slide-show>
  `

    return slideShow
  }

  // ----------------- Render -----------------
  async renderQuickView(handle, triggerBtn) {
    const res = await fetch(`/products/${handle}.js`);
    const product = await res.json();
    
    this.setAttribute("data-product-handle", handle);
    let productJson = document.createElement('div');
    productJson.classList.add("product-json");
    productJson.innerHTML = JSON.stringify(product);
    this.prepend(productJson);
    
    // IDs & text
    this.form?.setAttribute('id', `product_form_${product.id}`);
    this.titleLink?.setAttribute('href', product.url);
    if (this.titleEl) this.titleEl.textContent = product.title;
    if (this.qtyEl) this.qtyEl.setAttribute('max', '100');

    const stripHTML = (s) => (s == null ? '' : String(s).replace(/(<([^>]+)>)/gi, ''));  

    const idInput = this.modal.querySelector('#quickview_product_id');
    if (idInput) idInput.value = product.variants[0]?.id || '';

    // Prices & stock
    this._setPrice(this.priceEl, product.price, product.compare_at_price);
    const isPreorder = (product.tags || []).includes('preorder');
    this._setStock(product.available, isPreorder);

    // Ensure QV buttons don't act as native submit
    this.modal.querySelectorAll('.product-add, .product-buy').forEach(b => {
      b.setAttribute('type', 'button');
    });
    
    if(this.storeInfo){     
      this.storeInfo.innerHTML = `<a href='${product.url}' rel='nofollow' class='contact_link'>View Store Info</a>`
    }

    // Variant select
    if (this.variantSelect) {
      this.variantSelect.innerHTML = '';
      product.variants.forEach((v,index) => {
        const opt = document.createElement('option');
        if(triggerBtn){
          let cardOptions = triggerBtn.closest(".product_item_main").querySelectorAll(".product-variant-select option")
          if(cardOptions){
            let matchingOption = Array.from(cardOptions).find(option=> option.value == v.id)
            let cardQuantity = matchingOption.getAttribute("data-quantity")
            opt.setAttribute("data-quantity",cardQuantity)
          }
        }
        opt.value = v.id;
        opt.innerHTML = `${v.title} - ${newFormatMoney(v.price)}`;
        this.variantSelect.append(opt);
      });
    }

    // Options (radios)
    if (this.optionsList) {
      this.optionsList.innerHTML = '';
      product.options.forEach((opt) => {
        const field = document.createElement('fieldset');
        const legend = document.createElement('legend');
        const valSpan = document.createElement('span');
        valSpan.classList.add('option_val');
        legend.textContent = `${opt.name}:`;
        legend.append(valSpan);

        const ul = document.createElement('ul');
        opt.values.forEach((value) => {
          const li = document.createElement('li');
          li.innerHTML = `
            <div class="radio_input size_input" tabindex="0">
              <input type="radio" value="${value}" id="quickview__${opt.name}--${value}" name="${opt.name}" />
              <label for="quickview__${opt.name}--${value}">${value}</label>
            </div>`;
          ul.append(li);
        });
        field.append(legend, ul);
        this.optionsList.append(field);
      });
    }

    // Default first radio per group + change handlers
    const radios = this.modal.querySelectorAll("#quickview_options input[type='radio']");
    const ensureDefaultPerGroup = () => {
      const groups = new Map();
      radios.forEach(r => {
        if (!groups.has(r.name)) groups.set(r.name, []);
        groups.get(r.name).push(r);
      });
      groups.forEach(list => {
        const first = list[0];
        if (first) {
          first.checked = true;
          first.closest('fieldset')?.querySelector('.option_val')?.replaceChildren(document.createTextNode(first.value));
        }
      });
    };
    ensureDefaultPerGroup();

    // Dispatch initial variant-change for the default variant
    const firstVariant = product.variants[0];
    if (firstVariant) {
      if (idInput) idInput.value = firstVariant.id;
      this.modal.dispatchEvent(
        new CustomEvent('variant-change', { detail: { variant: firstVariant }, bubbles: true })
      );
    }

// 🔹 INITIAL availability gating after defaults
    const optionsContainer = this.modal.querySelector('.optionsList') || this.optionsList || this.modal;
    this.updateVariantStatuses(product.variants, optionsContainer);

    radios.forEach((radio) => {
      radio.addEventListener('change', () => {
        const selected = Array.from(this.modal.querySelectorAll("#quickview_options input[type='radio']:checked"))
          .map(r => r.value);

        radio.closest('fieldset')?.querySelector('.option_val')?.replaceChildren(document.createTextNode(radio.value));

        const matched = product.variants.find(v => selected.every((val, i) => v.options[i] === val));
        
         // NEW: announce variant to listeners (e.g., <buy-buttons>)
          this.modal.dispatchEvent(
            new CustomEvent('variant-change', { detail: { variant: matched ? matched : undefined }, bubbles: true })
          );

        if (matched) {
          idInput && (idInput.value = matched.id);
          const selectEl = this.modal.querySelector('#quickview_variant_select');
          if (selectEl) selectEl.value = matched.id;

           // swap image
           if (matched.featured_image) {
             this.imgEl.querySelector("img")?.setAttribute('src', matched.featured_image.src);
             this.imgEl.querySelector("img")?.setAttribute('alt', matched.featured_image.alt || '');
             this.imgEl.querySelector(".swiper").swiper.slideTo(0)
          }

          // price & stock
          this._setPrice(this.priceEl, matched.price, matched.compare_at_price);
          this._setStock(matched.available, isPreorder);         

          // url param
          const params = new URLSearchParams(window.location.search);
          params.set('variant', matched.id);
          window.history.replaceState(null, null, `${window.location.pathname}?${params.toString()}`);

            // 🔹 Recompute availability after every change
          this.updateVariantStatuses(product.variants, optionsContainer);
        } else {
          this._setStock(false, false);
        }
      });
    });

    // Main image
  if (this.imgEl && product.images && product.images.length > 0) {
   this.imgEl.innerHTML = this.sliderHTML(product.images, product.title);

   let slideShow = this.imgEl.querySelector("slide-show");
  
  if (slideShow) {
    if(customElements.get("slide-show")){
    // Ensure it's upgraded
    customElements.upgrade(slideShow);

    // Wait for it to be ready
    if ('whenDefined' in customElements) {
      customElements.whenDefined("slide-show").then(() => {
          slideShow.init(); // call your init logic        
      });
    }
    }   
  }
}

    // Stock badges initial sync
    const text = this.stockEl?.textContent?.trim();
    if (text === 'In Stock') {
      this.inStockEl && (this.inStockEl.style.display = 'block');
      this.noStockEl && (this.noStockEl.style.display = 'none');
    } else if (text === 'Out of Stock') {
      this.noStockEl && (this.noStockEl.style.display = 'block');
      this.inStockEl && (this.inStockEl.style.display = 'none');
    }

    // Mirror colors from the clicked product card (if available)
    this._applyColorSwatchesFromCard(triggerBtn);
  }
}

if(!customElements.get("quick-view")){
  customElements.define('quick-view', QuickView);
}
